import { CircularProgress } from "@mui/material";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { useToken } from "../../../server/auth/use-token";
import { IconCard } from "../../generic/components/icon-card";
import type { CsvUploadState, ProcessingState, RemoteInfo } from "../types";
import { Finished } from "./states/finished";
import { NotStarted } from "./states/not-started";

export const CsvUploadCard: FC<CsvUploadState> = (initialState) => {
  const [state, setState] = useState<CsvUploadState>(initialState);
  const [lastFile, setLastFile] = useState<File>();
  const setProcessingState = (processingState: ProcessingState): void =>
    setState((prev) => ({ ...prev, processingState }));

  const getToken = useToken();

  const onDrop = useCallback(
    async (file: File): Promise<void> => {
      if (!file || file.type !== "text/csv") {
        return setProcessingState("invalid-file");
      }
      const ack = state.processingState === "warning-acked" ? "?warning=ack" : "";
      setLastFile(file);
      setProcessingState("uploading");
      await state.onUpload();
      try {
        const request = await fetch(
          import.meta.env?.VITE_SERVICE_ENVIRONMENT === ""
            ? `https://employee-engine-prod-931411823909.us-east4.run.app${ack}`
            : `https://employee-engine-staging-943282600155.us-west2.run.app${ack}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${await getToken()}`,
            },
            body: file,
          },
        );
        const remoteInfo: RemoteInfo = await request.json();
        setState((prev) => ({ ...prev, remoteInfo }));
        console.log(request.status);
        if (request.status === 200) {
          setProcessingState("complete");
          state.onComplete();
        } else {
          if (remoteInfo.errors?.length > 0 || remoteInfo.exception) {
            setProcessingState("error");
          } else {
            setProcessingState("warning");
          }
        }
      } catch {
        setProcessingState("error");
      }
    },
    [state, getToken],
  );

  useEffect(() => {
    if (state.processingState !== "warning-acked") return;
    if (!lastFile) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void onDrop(lastFile).catch(console.error);
  }, [lastFile, onDrop, state]);

  switch (state.processingState) {
    case "not started":
      return onDrop ? <NotStarted onDrop={(v) => void onDrop(v).catch(console.error)} /> : null;
    case "uploading":
    case "warning-acked":
      return (
        <IconCard
          title="Uploading..."
          icon={{ icon: <CircularProgress />, sx: { bgcolor: "#edf6ee" } }}
        />
      );
    case "complete":
    case "error":
    case "warning":
    case "invalid-file":
      return (
        <Finished
          state={state.processingState}
          remoteInfo={state.remoteInfo}
          setProcessingState={(processingState: ProcessingState) =>
            setState((prev) => ({ ...prev, processingState }))
          }
        />
      );
  }
};
